import { catchError, map,  first, takeUntil } from 'rxjs/operators';
import { StreamSourceService } from './../services/streamSource.service';
import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy } from "@angular/core";
import { FormGroup, FormArray, FormControl, Validators, AsyncValidatorFn, AbstractControl, ValidationErrors } from "@angular/forms";
import * as _ from 'lodash';
import { TenantService } from "../services/tenant.service";
import { Subject, Observable, of } from "rxjs";
import { UserService } from '../services/user.service';

@Component({
    selector: 'topic-builder-publish',
    styles: [`
        mat-card-content div {
            padding-top: 10px;
        }

        label.chip-label {
            color: rgba(0, 0, 0, 0.54);
            font-size: smaller;
            display: block;
            padding-bottom: 10px;
        }
    `],
    template: `
        <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner syle="padding-top: 100px;"></spinner>
        </div>
        <mat-card *ngIf="!noTouching && form" [formGroup]="form">
            <mat-card-content>
                <div fxLayout="row" *ngIf="!noPublishing">
                    <mat-slide-toggle color="primary" formControlName="published">Publish</mat-slide-toggle>
                </div>
                <div>
                    <mat-form-field fxFlex.lt-lg="200px" fxFlex.gt-md="500px">
                        <input matInput formControlName="title" placeholder="Name">
                        <mat-error *ngIf="form.get('title').hasError('required')">Name is required</mat-error>
                    </mat-form-field>
                </div>
                <div>
                    <mat-form-field fxFlex.lt-lg="200px" fxFlex.gt-md="500px">
                        <textarea matInput formControlName="description" placeholder="Description"></textarea>
                    </mat-form-field>
                </div>
                <div *ngIf="categorizationEnabled">
                    <label class="chip-label">Categories</label>
                    <topic-category-manager [topic]="topic" (changed)="categoriesChanged()"></topic-category-manager> 
                </div>
                <div>
                    <label class="chip-label" style="padding-bottom: 0px;">Tags</label>
                    <topic-tag-manager [topic]="topic" (changed)="tagsChanged()"></topic-tag-manager>
                </div>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Advanced</mat-panel-title>
                    </mat-expansion-panel-header>
                    <div>
                        <mat-slide-toggle color="primary" formControlName="rssEnabled">Enable RSS</mat-slide-toggle>
                    </div>
                    <div>
                        <mat-slide-toggle color="primary" formControlName="emailPostingEnabled" style="padding-top: 20px; padding-bottom: 20px;">Enable email posting</mat-slide-toggle>
                        <mat-form-field *ngIf="form.get('emailPostingEnabled').value" style="padding-left: 25px; width: 400px;">
                            <input matInput formControlName="emailPrefix" placeholder="Email prefix"><span matSuffix>@{{streamsEmailDomain}}</span>
                            <mat-error *ngIf="form.get('emailPrefix').hasError('required')">Email prefix is required</mat-error>
                            <mat-error *ngIf="form.get('emailPrefix').hasError('pattern')">Only type the part of the email address before the @ symbol and no spaces.</mat-error>
                            <mat-error *ngIf="form.get('emailPrefix').hasError('maxLength')">Email address prefix must be shorter than 25 characters.</mat-error>            
                            <mat-error *ngIf="form.get('emailPrefix').hasError('uniqueStreamEmail')">The email address you have entered is already in use.</mat-error>
                        </mat-form-field>
                    </div>

                </mat-expansion-panel>
            </mat-card-content>
        </mat-card>
    `
})
export class TopicBuilderPublishComponent implements OnInit, OnChanges, OnDestroy {
    private cleanupTime = new Subject<void>();
    @Input() form: FormGroup;
    @Input() selected: boolean;
    topic: { taxonomicMemberships?, tags?} = {};
    noTouching: boolean;
    streamsEmailDomain: any;
    categorizationEnabled: boolean;
    noPublishing: boolean;
    
    constructor(private streamSourceService: StreamSourceService, 
                tenantService: TenantService,
                userService: UserService) {
        tenantService.getCurrentTenant().pipe(first())
        .subscribe(tenant => {
            let hideCategories = false;
            if (tenant.settings.autoTopicPublish !== 'true') {
                if (!userService.isCurrentUserAdmin()) {
                    this.noPublishing = true;
                    hideCategories = true;
                    this.form.get('published').setValue(false);   
                }
            }
            this.categorizationEnabled = tenant.settings.hideCategories !== 'true' && !hideCategories; 
            this.streamsEmailDomain = tenant.settings.streamsEmailDomain; 
        });
    }

    ngOnInit(): void {
        this.form.get('emailPostingEnabled').valueChanges
        .pipe(takeUntil(this.cleanupTime))
        .subscribe(emailPostingEnabled => {
            const control = this.form.get('emailPrefix');
            control.setValidators(emailPostingEnabled ? [Validators.required, Validators.maxLength(25), Validators.pattern('^[A-Za-z0-9_-]+$')] : []);
            control.setAsyncValidators(emailPostingEnabled ? [this.uniqueStreamEmail] : []);
            control.updateValueAndValidity();
        });

        this.form.get('emailPrefix').valueChanges
        .pipe(takeUntil(this.cleanupTime))
        .subscribe(emailPrefix => { this.form.get('emailPostingAddress').setValue(emailPrefix + '@' + this.streamsEmailDomain); });
    }

    private get uniqueStreamEmail(): AsyncValidatorFn {
        return   (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => 
            this.streamSourceService.searchStreamSources('uri', control.value + '@' + this.streamsEmailDomain)
            .pipe(
                map(() => ({ uniqueStreamEmail: { valid: false } })),
                catchError(err =>  err.status === 404 ? of(null) : of({ uniqueStreamEmail: { valid: false } }))
            );
    }

    tagsChanged(): void {
        this.repopulate('tags', this.topic.tags) 
    }
    
    categoriesChanged(): void {
        this.repopulate('taxonomicMemberships', this.topic.taxonomicMemberships);
    }

    private repopulate(key, set): void {
        const tagArray: FormArray = <FormArray>this.form.get(key);
        tagArray.controls.splice(0);
        _.forEach(set, tag => { tagArray.push(new FormControl(tag)); });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selected.currentValue) {
            this.topic.taxonomicMemberships = this.form.get('taxonomicMemberships').value;
            this.topic.tags = this.form.get('tags').value;
        }
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}